import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import PageHeader from './components/PageHeader';
import PersonalInfoSection from './components/PersonalInfoSection';
import OrganizationalInfoSection from './components/OrganizationalInfoSection';
import ProfessionalInfoSection from './components/ProfessionalInfoSection';
import ContactInfoSection from './components/ContactInfoSection';
import FormActions from './components/FormActions';
import httpHelper, { Method_TYPE } from 'utils/http-helper';
import URL_CONSTANTS from 'constants/url-constants';

const AddEditMember = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [memberId, setMemberId] = useState('');

  // Check if editing mode based on URL params or state
  const isEditing = location.state?.isEditing || false;
  const existingMember = location.state?.memberData || null;

  // Form data state
  const [formData, setFormData] = useState({
    // Personal Information
    name: '',
    gender: window.location.hostname.includes("mparthiseva.ssssotn.org") ? 'F' : 'M',
    dob: '',
    bloodGroup: '',
    mobNoPrimary: '',
    mobNoSecondry: '',

    // Organizational Information
    district: '',
    samithi: '',
    role: '',
    saiOneId: '',
    pmcNo: '',
    scNo: '',

    // Professional Information
    qualification: '',
    profession: '',
    skillSet: [],

    // Contact Information
    emailId: '',
    doorNo: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    pinCode: '',

    // Member status
    is_blocked: false
  });

  // Form validation errors
  const [errors, setErrors] = useState({});

  // Section collapse states
  const [sectionStates, setSectionStates] = useState({
    personal: false,
    organizational: false,
    professional: true,
    contact: true
  });

  // Mock data for dropdowns
  const districts = [
    { value: 'chennai', label: 'Chennai District' },
    { value: 'coimbatore', label: 'Coimbatore District' },
    { value: 'madurai', label: 'Madurai District' },
    { value: 'salem', label: 'Salem District' },
    { value: 'tiruchirappalli', label: 'Tiruchirappalli District' },
    { value: 'tirunelveli', label: 'Tirunelveli District' },
    { value: 'vellore', label: 'Vellore District' },
    { value: 'erode', label: 'Erode District' }
  ];

  const [samithis, setSamithis] = useState([]);

  const samithisByDistrict = {
    chennai: [
      { value: 'adyar', label: 'Adyar Samithi' },
      { value: 'mylapore', label: 'Mylapore Samithi' },
      { value: 'velachery', label: 'Velachery Samithi' },
      { value: 'tambaram', label: 'Tambaram Samithi' },
      { value: 'chrompet', label: 'Chrompet Samithi' }
    ],
    coimbatore: [
      { value: 'rs_puram', label: 'RS Puram Samithi' },
      { value: 'gandhipuram', label: 'Gandhipuram Samithi' },
      { value: 'peelamedu', label: 'Peelamedu Samithi' }
    ],
    madurai: [
      { value: 'anna_nagar', label: 'Anna Nagar Samithi' },
      { value: 'kk_nagar', label: 'KK Nagar Samithi' },
      { value: 'thiruparankundram', label: 'Thiruparankundram Samithi' }
    ]
  };

  const roles = [
    { value: 'MB', label: 'Member' },
    { value: 'CD', label: 'Coordinator' },
    { value: 'LD', label: 'Leader' },
    { value: 'SD', label: 'Sevadal' },
    { value: 'AW', label: 'Active Worker' },
    { value: 'YC', label: 'Youth Coordinator' },
    { value: 'MC', label: 'Mahila Coordinator' }
  ];

  const qualifications = [
    { value: 'sslc', label: 'SSLC' },
    { value: 'hsc', label: 'HSC' },
    { value: 'diploma', label: 'Diploma' },
    { value: 'bachelor', label: 'Bachelor\'s Degree' },
    { value: 'master', label: 'Master\'s Degree' },
    { value: 'phd', label: 'PhD' },
    { value: 'professional', label: 'Professional Certification' },
    { value: 'Others', label: 'Others' },
  ];

  const professions = [
    { value: 'student', label: 'Student' },
    { value: 'driver', label: 'Driver' },
    { value: 'plumper', label: 'Plumper' },
    { value: 'electrician', label: 'Electrician' },
    { value: 'teacher', label: 'Teacher' },
    { value: 'engineer', label: 'Engineer' },
    { value: 'doctor', label: 'Doctor' },
    { value: 'business', label: 'Business' },
    { value: 'government', label: 'Government Employee' },
    { value: 'retired', label: 'Retired' },
    { value: 'homemaker', label: 'Homemaker' },
    { value: 'other', label: 'Other' }
  ];

  const skillSets = [
    { value: 'cooking', label: 'Cooking' },
    { value: 'first_aid', label: 'First Aid' },
    { value: 'music', label: 'Music' },
    { value: 'decoration', label: 'Decoration' },
    { value: 'electrical', label: 'Electrical' },
    { value: 'plumbing', label: 'Plumbing' },
    { value: 'driving', label: 'Driving' },
    { value: 'computer', label: 'Computer Knowledge' },
    { value: 'photography', label: 'Photography' },
    { value: 'sound_system', label: 'Sound System' },
    { value: 'crowd_management', label: 'Crowd Management' },
    { value: 'registration', label: 'Registration' },
    { value: 'transport', label: 'Transport' },
    { value: 'medical', label: 'Medical' },
    { value: 'security', label: 'Security' },
    { value: 'cleaning', label: 'Cleaning' }
  ];

  // Load existing member data if editing
  useEffect(() => {
    if (isEditing && existingMember) {
      existingMember.skillSet = 'electrical,plumbing,driving';
      console.log("existingMember", existingMember.samithi?._id);
      setMemberId(existingMember._id || '');
      setFormData({
        name: existingMember.name || '',
        gender: existingMember.gender || '',
        dob: existingMember.dob || '',
        bloodGroup: existingMember.bloodGroup || '',
        mobNoPrimary: existingMember.mobNoPrimary || '',
        mobNoSecondry: existingMember.mobNoSecondry || '',
        aadharNo: existingMember.aadharNo || '',
        samithi: existingMember.samithi?._id ? existingMember.samithi._id.toString() : (existingMember.samithi ? existingMember.samithi.toString() : ''),
        district: '',
        role: existingMember.role || '',
        saiOneId: existingMember.saiOneId || '',
        pmcNo: existingMember.pmcNo || '',
        scNo: existingMember.scNo || '',
        qualification: existingMember.qualification || '',
        profession: existingMember.profession || '',
        skillSet: existingMember.skillSet?.split(',') || [],
        emailId: existingMember.emailId || '',
        doorNo: existingMember.doorNo || '',
        addressLine1: existingMember.addressLine1 || '',
        addressLine2: existingMember.addressLine2 || '',
        city: existingMember.city || '',
        pinCode: existingMember.pinCode || '',
        is_blocked: Boolean(
          existingMember.is_blocked === true ||
          existingMember.is_blocked === 'true' ||
          existingMember.is_blocked === 1 ||
          existingMember.isBlocked === true ||
          existingMember.isBlocked === 'true' ||
          existingMember.isBlocked === 1
        )
      });
    }
  }, [isEditing, existingMember]);

  // Update samithis when district changes
  // useEffect(() => {
  //   if (formData.district) {
  //     setSamithis(samithisByDistrict[formData.district] || []);
  //     if (formData.samithi && !samithisByDistrict[formData.district]?.find(s => s.value === formData.samithi)) {
  //       setFormData(prev => ({ ...prev, samithi: '' }));
  //     }
  //   } else {
  //     setSamithis([]);
  //   }
  // }, [formData.district]);

  useEffect(() => {

    const fetchSamithiInfo = async () => {

      try {


        const response = await httpHelper(`${URL_CONSTANTS.SAMITHI_INFO}`, Method_TYPE.GET);

        const samithiData = response.data.map(samithi => {
          samithi.label = samithi.samithiName + (samithi.district ? ` (${samithi.district.districtName})` : '');
          samithi.keyValue = samithi.samithiName;
          samithi.value = samithi._id.toString();
          return samithi;
        })

        setSamithis(samithiData);

        // setLoading(false);

      } catch (error) {

        console.error('Error fetching data:', error);

        setSamithis([])


      }
    };
    fetchSamithiInfo()
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const toggleSection = (section) => {
    setSectionStates(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Personal Information validation
    if (!formData.name.trim()) newErrors.name = 'Full name is required';
    if (!formData.gender) newErrors.gender = 'Gender is required';
    // if (!formData.dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
    if (!formData.mobNoPrimary.trim()) newErrors.mobNoPrimary = 'Mobile number is required';
    else if (!/^\d{10}$/.test(formData.mobNoPrimary)) newErrors.mobNoPrimary = 'Mobile number must be 10 digits';

    // Organizational Information validation
    // if (!formData.district) newErrors.district = 'District is required';
    if (!formData.samithi) newErrors.samithi = 'Samithi is required';
    // if (!formData.role) newErrors.role = 'Role is required';
    if (!formData.saiOneId.trim()) newErrors.saiOneId = 'Sai Connect ID is required';

    // Contact Information validation
    // if (!formData.email.trim()) newErrors.email = 'Email is required';
    // else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email format';
    // if (!formData.addressLine1.trim()) newErrors.addressLine1 = 'Address line 1 is required';
    // if (!formData.city.trim()) newErrors.city = 'City is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const saveMember = async () => {

    const districtId = samithis.find(s => s._id.toString() === formData.samithi.toString())?.district?._id || '';

    formData.district = districtId;

    if (formData.aadharNo) {
      formData.aadharNo = formData.aadharNo.toString().trim();
      formData.aadharNo = formData.aadharNo.toString().replace(/\s/g, '');
    }
    if (formData.mobNoPrimary) {
      formData.mobNoPrimary = formData.mobNoPrimary.toString().trim();
      formData.mobNoPrimary = formData.mobNoPrimary.toString().replace(/\s/g, '');
    }
    if (formData.mobNoSecondry) {
      formData.mobNoSecondry = formData.mobNoSecondry.toString().trim();
      formData.mobNoSecondry = formData.mobNoSecondry.toString().replace(/\s/g, '');
    }
    if (formData.scNo) {
      formData.scNo = formData.scNo.toString().trim();
      formData.scNo = formData.scNo.toString().replace(/\s/g, '');
    }

    formData.is_blocked = Boolean(formData.is_blocked);

    await new Promise(resolve => setTimeout(resolve, 2000));

    if (formData.skillSet && Array.isArray(formData.skillSet)) {
      formData.skillSet = formData?.skillSet?.join(',');
    }


    if (isEditing) {

     return await httpHelper(`${URL_CONSTANTS.MEMBER}/${memberId}`, Method_TYPE.PUT, formData);

    }
    else {

      return await httpHelper(`${URL_CONSTANTS.MEMBER}`, Method_TYPE.POST, formData);

    }

  }

  const handleSave = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      // Simulate API call

      const memberResult = await saveMember()

      alert( isEditing ? 'Member updated successfully!' : 'Member added successfully!')

      // Navigate back to dashboard or previous page
      navigate('/dashboard', {
        state: {
          message: isEditing ? 'Member updated successfully!' : 'Member added successfully!',
          type: 'success'
        }
      });
    } catch (error) {
      console.error('Error saving member:', error);
      alert(error)
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveAndEnroll = async () => {
    if (!validateForm()) return;

    if (formData.is_blocked) {
      alert("Blocked members cannot be enrolled for service.");
      return;
    }

    setIsLoading(true);
    
    try {
      // Simulate API call

      const memberResult= await saveMember();

      const memberData = memberResult.data;

      const enrollObj = !isEditing ? {
        member: memberData._id,
        samithi: memberData.samithi || "",
        district: memberData.district || "",
      } : {
        member:memberId,
        samithi: formData.samithi || "",
        district: formData.district || "",
      }

      if(enrollObj){
        
        const data = await httpHelper(URL_CONSTANTS.CURRENT_BATCH, Method_TYPE.GET);

        const batchId = data?._id;
        
        const response =  await httpHelper(`${URL_CONSTANTS.SERVICE}memberallocatedcount/batch/${batchId}/member/${enrollObj.member}`,Method_TYPE.GET);

        if(response > 0) {

            // setShowModal(true)

            alert("Member already enrolled for this batch.");

            return;
        }

        const body = {
           batch: batchId,
           member: enrollObj.member || "",
           samithi: enrollObj.samithi || "",
           district: enrollObj.district || "",
        }

         const enrollResponse = await httpHelper(URL_CONSTANTS.SERVICE , Method_TYPE.POST, body);

         alert("Member Added and enrolled successfully!")

      }

      // Navigate back to dashboard or previous page
      navigate('/dashboard', {
        state: {
          message: isEditing ? 'Member updated successfully!' : 'Member added successfully!',
          type: 'success'
        }
      });
    } catch (error) {
      console.error('Error saving member:', error);
      alert(error)
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Breadcrumb />

      <main className="pt-4 pb-8 px-4 lg:px-6">
        <div className="max-w-4xl mx-auto">
          <PageHeader
            isEditing={isEditing}
            memberName={formData.name || 'New Member'}
            isBlocked={Boolean(formData.is_blocked)}
          />

          <form onSubmit={(e) => e.preventDefault()}>
            <PersonalInfoSection
              formData={formData}
              handleInputChange={handleInputChange}
              errors={errors}
              isCollapsed={sectionStates.personal}
              onToggleCollapse={() => toggleSection('personal')}
            />

            <OrganizationalInfoSection
              formData={formData}
              handleInputChange={handleInputChange}
              errors={errors}
              isCollapsed={sectionStates.organizational}
              onToggleCollapse={() => toggleSection('organizational')}
              districts={districts}
              samithis={samithis}
              roles={roles}
            />

            <ProfessionalInfoSection
              formData={formData}
              handleInputChange={handleInputChange}
              errors={errors}
              isCollapsed={sectionStates.professional}
              onToggleCollapse={() => toggleSection('professional')}
              qualifications={qualifications}
              professions={professions}
              skillSets={skillSets}
            />

            <ContactInfoSection
              formData={formData}
              handleInputChange={handleInputChange}
              errors={errors}
              isCollapsed={sectionStates.contact}
              onToggleCollapse={() => toggleSection('contact')}
            />

            <FormActions
              isEditing={isEditing}
              isLoading={isLoading}
              onSave={handleSave}
              onSaveAndEnroll={handleSaveAndEnroll}
              onCancel={handleCancel}
              isBlocked={Boolean(formData.is_blocked)}
            />
          </form>
        </div>
      </main>
    </div>
  );
};

export default AddEditMember;